import { useEffect, useState } from "react";
import { ExerciseType } from "@/components/v3/exercises/ExerciseBadge";
import { SpinnerLoader } from "@/components/ui/Loader.tsx";
import { useBreakpoint } from "@/utils/hooks/useBreakpoints";
import { useUserExercises } from "@/data/exercises.ts";
import { useExerciseAction } from "@/contexts/exercise/Context";
import {
  Navigate,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  ExerciseDONE,
  ExerciseTODO,
  IGrammarExercise,
  ExerciseCorrectionItem,
} from "@/types";
import routes from "@/config/routes.ts";
import WarmUp from "@/components/exercises/grammar/Desktop/WarmUp.tsx";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import AnswerSheet from "@/components/v3/exercises/AnswerSheet";
import WarmUpMobile from "@/components/exercises/grammar/Mobile/WarmUp";
import ExerciseLayout from "@/layouts/ExerciseLayout.tsx";
import ExerciseLayoutMobile from "@/layouts/ExerciseLayoutMobile";
import PotExerciseResultModal from "@/components/exercises/grammar/Desktop/PotExerciseResultModal";

const GrammarExercise = (): JSX.Element => {
  const { isPot: isPotFromGlobals } = useGlobals();
  // get isDone and isPot from query params
  const [searchParams] = useSearchParams();
  const isDone = searchParams.get("isDone");
  const isPotFromQuery = searchParams.get("isPot");
  const isPot = isPotFromGlobals || isPotFromQuery;
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const isSmall = ["xs", "sm", "md"].indexOf(breakpoint) !== -1;
  const { id } = useParams();
  const { exercises, isLoading } = useUserExercises();
  const { updateExerciseResult, setExerciseId, endSequence, startSequence } =
    useExerciseAction();
  const [isTimerVisible, setIsTimerVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const exercise = exercises?.toDo?.find(
    (e: ExerciseTODO) =>
      e?.id?.toString() == id! || e?.token?.toString() == id!,
  ) as ExerciseTODO;
  const exerciseDone = exercises?.done?.find(
    (e: ExerciseDONE) =>
      e?.id?.toString() == id! || e?.token?.toString() == id!,
  ) as ExerciseDONE;
  const inProgress = exercise?.exercises_statuses?.status === "in progress";
  const currentTime =
    inProgress && exercise?.exercises_progress
      ? exercise?.exercises_progress.current_time
      : 0;
  const timer = inProgress ? Math.floor(currentTime / 60) : exercise?.duration;
  const type = exercise?.type;

  useEffect(() => {
    if (isPot && isDone && exerciseDone) {
      setIsModalOpen(true);
    } else {
      setIsModalOpen(false);
    }
  }, [isPot, isDone, exerciseDone]);

  useEffect(() => {
    if (
      inProgress &&
      exercise &&
      exercise?.pivot &&
      exercise?.pivot?.correction
    ) {
      try {
        const corrections = JSON.parse(
          exercise?.pivot?.correction,
        ) as ExerciseCorrectionItem[];

        updateExerciseResult(corrections);
      } catch (error) {
        console.error(
          "Errore durante il parsing dei dati di correzione:",
          error,
        );
      }
    }
  }, [exercise, inProgress, updateExerciseResult]);

  useEffect(() => {
    if (!exercise) return;
    setExerciseId(exercise.id);
  }, [exercise, setExerciseId]);

  const handleClose = () => {
    endSequence();
    navigate(routes.exercises);
  };

  const handleStart = () => {
    // if (!timer) {
    //   console.error("The timer is not set or 0!", timer);
    //   return;
    // }

    const correction = exercise?.pivot?.correction ?? "[]";

    let newExerciseIndex: Array<{ type: string }> = [];

    if (typeof correction == "string") {
      newExerciseIndex = JSON.parse(correction) as Array<{ type: string }>;

      if (!Array.isArray(newExerciseIndex)) {
        newExerciseIndex = [];
      }
    }

    startSequence(Date.now(), (timer ?? 1) * 60, newExerciseIndex.length);
    setIsTimerVisible(true);
  };

  const getTypeFromValue = (typeValue: string): ExerciseType => {
    const typeKey = Object.keys(ExerciseType).find(
      (key) =>
        ExerciseType[key as keyof typeof ExerciseType]?.toLowerCase() ===
        typeValue?.toLowerCase(),
    );
    return typeKey
      ? ExerciseType[typeKey as keyof typeof ExerciseType]
      : ExerciseType.READING;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <SpinnerLoader className="!h-12 !w-12 border-t-light" />
      </div>
    );
  }

  // If it's a POT exercise that's done, we only show the modal
  if (isPot && isDone && isModalOpen) {
    return (
      <PotExerciseResultModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          navigate(routes.exercises);
        }}
        exercise={exerciseDone as unknown as IGrammarExercise}
        userAnswers={exercises?.userAnswers ?? {}}
      />
    );
  }

  if (!exercises?.toDo?.length && !exercises?.done?.length) {
    return <Navigate to={routes.exercises} />;
  }

  if (isSmall && isPot) {
    return (
      <ExerciseLayoutMobile
        type={getTypeFromValue(type)}
        timer={timer}
        title={exercise?.name}
        onClose={handleClose}
        isGrammar={isPot as boolean}
        inProgress={inProgress}
        isTimerVisible={isTimerVisible}
      >
        <WarmUpMobile
          isDone={Boolean(isDone)}
          onStart={handleStart}
          exercise={exercise}
          inProgress={inProgress}
        />
      </ExerciseLayoutMobile>
    );
  }

  if (!isPot) {
    return (
      <ExerciseLayout
        type={getTypeFromValue(type)}
        title={exercise?.name}
        onClose={() => navigate(-1)}
        exercise={exercise}
      >
        {isLoading ? (
          <div className="flex items-center justify-center w-full h-full">
            <SpinnerLoader className="!h-12 !w-12 border-t-light" />
          </div>
        ) : (
          <AnswerSheet exercise={exercise} id={exercise ? exercise.id : 0} />
        )}
      </ExerciseLayout>
    );
  }

  return (
    <ExerciseLayout
      type={getTypeFromValue(type)}
      timer={timer}
      title={exercise?.name}
      onClose={handleClose}
      isGrammar={isPot as boolean}
      inProgress={inProgress}
    >
      <WarmUp
        isDone={Boolean(isDone)}
        exercise={exercise}
        inProgress={inProgress}
      />
    </ExerciseLayout>
  );
};

export default GrammarExercise;
